// Module 05 — prosumer battery policy + runtime projection. Pure function of (load, batt, env,
// price). Strict R6 priority: charge-when-cheap → serve own load behind-the-meter → export
// surplus → keep a reserve floor (deeper discharge only on FLISR critical restore). One tick = 1h.
//
// LOCATIONAL value ranking (economics overhaul, build log 45) for a surplus kWh, by net value:
//   1. SELF-CONSUME now  — avoids the full retail bill (commodity + delivery + Tx + loss): highest.
//   2. STORE for own peak — captures the TOU/ULO arbitrage spread × √η (charge cheap, discharge peak).
//   3. NEIGHBOUR P2P      — the surplus this fn emits as exportMw is offered to the local market, which
//                           prices a neighbour trade at commodity + half the avoided delivery (see
//                           market.ts) — ABOVE grid export, BELOW the buyer's import.
//   4. GRID export        — net-metering credits the COMMODITY rate only (no delivery value): lowest.
// This fn produces the self-consume/store/export split; market.clearLocalMarket then routes exportMw to
// a neighbour (P2P) before it falls through to the grid, so the ranking emerges from the two together.
//
// P14 (C113): a reservation-price gate sits ON TOP of the coarse peak band — discharge fires only when
// the price clears the site's opportunity cost (chargeCost/√η + reservationMargin). Default η=1 ⇒
// reservation = CHEAP + margin, well below peak ⇒ legacy/golden discharge behaviour is byte-identical.
// P14 (C58): the round-trip η loss is surfaced on ProsumerRuntime.roundTripLossMwh for BILLING.
import type { Load, EnvSample, PriceSample, ProsumerRuntime } from "../domain/index";
import type { BatteryState, ProsumerDecision } from "./types";
import pricing from "./pricing.json";

const TICK_HOURS = 1;
// exported so the engine's grid-BESS off-peak charging uses the SAME cheap-price signal the
// prosumer policy charges on (one threshold, one source: pricing.json — R14).
export const CHEAP_PRICE_PER_KWH: number = pricing.signalThresholds.cheapPerKwh;
const CHEAP_PER_KWH = CHEAP_PRICE_PER_KWH;
const PEAK_PER_KWH = pricing.signalThresholds.peakPerKwh;
const RESERVE_FRAC = pricing.reserveFloorFrac;
const RESERVATION_MARGIN_PER_KWH = pricing.signalThresholds.reservationMarginPerKwh;

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

export function prosumerPolicy(
  load: Load, batt: BatteryState, env: EnvSample, price: PriceSample,
  opts?: { restoreCriticalLoad?: boolean },
): ProsumerDecision {
  // v3: rooftopSuitabilityScore (restored Debug 3 attr) derates PV — shading/orientation losses.
  const solarMw = (load.peakSolarMw ?? 0) * env.solarCF * (load.rooftopSuitabilityScore ?? 1);
  const demandMw = load.maxDemandMw * env.loadMult(load);

  // Solar always serves own load first (behind-the-meter), before any battery action or export.
  const selfFromSolar = Math.min(solarMw, demandMw);
  const solarSurplus = solarMw - selfFromSolar;
  const residualDemand = demandMw - selfFromSolar;
  // P14 C113: per-site reserve floor override (absent ⇒ global). FLISR restore bypasses it to empty.
  const reserveFrac = opts?.restoreCriticalLoad ? 0 : (load.reserveFloorFrac ?? RESERVE_FRAC);
  const reserveMwh = reserveFrac * batt.capacityMwh;

  let chargeMw = 0;
  let dischargeMw = 0;
  let selfServeMw = selfFromSolar;
  let exportMw = solarSurplus;

  if (price.retailPerKwh <= CHEAP_PER_KWH) {
    // Cheap / overnight: charge battery (solar surplus first, then grid), export any leftover solar.
    const headroomMwh = batt.capacityMwh - batt.socMwh;
    const charge = Math.min(batt.chargeMw, headroomMwh / TICK_HOURS);
    const solarToBattery = Math.min(solarSurplus, charge);
    chargeMw = charge;
    exportMw = solarSurplus - solarToBattery;
  } else if (price.retailPerKwh >= PEAK_PER_KWH) {
    // Peak: discharge — serve residual own load first, export the remainder. Honour the reserve
    // floor unless this is a FLISR critical-load restoration (then discharge to empty).
    // P14 C113: the coarse peak band is the ELIGIBILITY gate; the reservation price is the FINE gate
    // on top. A battery only discharges if the price clears its opportunity cost (chargeCost/√η +
    // margin). η from load.batteryEtaAcAc (default 1 ⇒ reservation = CHEAP + margin ≪ peak ⇒ neutral).
    const eta = load.batteryEtaAcAc !== undefined && load.batteryEtaAcAc > 0 && load.batteryEtaAcAc <= 1
      ? load.batteryEtaAcAc : 1;
    const reservationPricePerKwh = CHEAP_PER_KWH / Math.sqrt(eta) + RESERVATION_MARGIN_PER_KWH;
    if (price.retailPerKwh >= reservationPricePerKwh) {
      const floorMwh = opts?.restoreCriticalLoad ? 0 : reserveMwh;
      const usableMwh = Math.max(0, batt.socMwh - floorMwh);
      const discharge = Math.min(batt.dischargeMw, usableMwh / TICK_HOURS);
      const toLoad = Math.min(discharge, residualDemand);
      dischargeMw = discharge;
      selfServeMw = selfFromSolar + toLoad;
      exportMw = solarSurplus + (discharge - toLoad);
    }
    // else: peak band but below the reservation price ⇒ HOLD (the opportunity cost is not recovered).
  }
  // else mid-price: hold battery; solar self-serves + exports surplus (defaults above).

  return { chargeMw, dischargeMw, selfServeMw, exportMw, solarMw };
}

// v3: the restored Debug 3 battery transition (MODEL_AND_TOPOLOGY §4.4). Round-trip AC-AC
// efficiency η splits symmetrically: charging stores chargeMw·√η, discharging drains
// dischargeMw/√η (so a full cycle returns η of what the grid put in). η omitted (legacy data,
// synthetic fixtures) ⇒ lossless, byte-identical to the old engine update. √ is hot-path-legal
// (powerflow rule: + − * / sqrt only). Returns the clamped next socMwh; the caller owns the state.
export function applyBatteryTransition(
  batt: BatteryState, dec: ProsumerDecision, etaAcAc?: number,
): number {
  const eta = etaAcAc !== undefined && etaAcAc > 0 && etaAcAc <= 1 ? etaAcAc : 1;
  const sqrtEta = Math.sqrt(eta);
  const deltaMwh = (dec.chargeMw * sqrtEta - dec.dischargeMw / sqrtEta) * TICK_HOURS;
  const next = batt.socMwh + deltaMwh;
  return next < 0 ? 0 : next > batt.capacityMwh ? batt.capacityMwh : next;
}

// The P14 C58 round-trip η loss on the BILLING side — energy that left the grid to fill the cells
// but never reached load (dischargeMw·(1/√η − 1)·TICK_HOURS). η default 1 ⇒ 0 ⇒ golden-neutral.
// P1 owns the net-load (energy) side; this is the $ (bill) side. Pure, hot-path-legal (sqrt only).
export function roundTripLossMwh(dec: ProsumerDecision, etaAcAc?: number): number {
  const eta = etaAcAc !== undefined && etaAcAc > 0 && etaAcAc <= 1 ? etaAcAc : 1;
  return dec.dischargeMw * (1 / Math.sqrt(eta) - 1) * TICK_HOURS;
}

// Read-off projection of the decision into the domain.v2 Snapshot.prosumers shape — no new
// physics, no re-sample. batteryMw is signed: >0 discharging, <0 charging.
export function prosumerRuntime(
  load: Load, batt: BatteryState, dec: ProsumerDecision,
): ProsumerRuntime {
  return {
    id: load.id,
    socFrac: clamp01(batt.capacityMwh > 0 ? batt.socMwh / batt.capacityMwh : 0),
    solarMw: dec.solarMw,
    batteryMw: dec.dischargeMw - dec.chargeMw,
    servedMw: dec.selfServeMw,
    exportMw: dec.exportMw,
    // P14 C58: surface the round-trip η loss for billing (0 when η default 1).
    roundTripLossMwh: roundTripLossMwh(dec, load.batteryEtaAcAc),
  };
}
